import { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, LogOut, TestTube2 } from 'lucide-react';
import { Button } from '../components/Button.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../lib/auth-store.js';
import { canUsePush, currentPermission, isStandalone, subscribe, unsubscribe } from '../lib/push.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';

interface Device {
  id: string;
  name: string;
  userAgent: string | null;
  hasPush: boolean;
  createdAt: number;
  lastSeenAt: number | null;
  current: boolean;
}

export function SettingsScreen() {
  const logout = useAuthStore((s) => s.logout);
  const [devices, setDevices] = useState<Device[]>([]);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [standalone, setStandalone] = useState<boolean>(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  useDocumentTitle(buildTitle('Настройки'));

  const load = useCallback(async () => {
    const data = await api.get<Device[]>('/api/v1/devices');
    setDevices(data);
    setPermission(await currentPermission());
    setStandalone(await isStandalone());
    setPushSupported(await canUsePush());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const current = devices.find((d) => d.current);
  const pushOn = current?.hasPush === true && permission === 'granted';

  async function enablePush() {
    setBusy(true);
    try {
      await subscribe(api);
      await load();
    } catch (err) {
      alert(`Не получилось подписаться: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function disablePush() {
    setBusy(true);
    try {
      await unsubscribe(api);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function testPush() {
    setBusy(true);
    try {
      await api.post('/api/v1/push/test');
      alert('Отправили тест-пуш. Если не пришёл — проверь, что включены пуши и PWA на Home Screen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-4 space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold pt-2">Settings</h1>

      <section className="space-y-3">
        <h2 className="text-xs uppercase text-neutral-500">Notifications</h2>
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4 space-y-3">
          {!pushSupported ? (
            <p className="text-sm text-neutral-400">
              Браузер не поддерживает Web Push. На iOS Push работает только из PWA,
              установленной на домашний экран.
            </p>
          ) : !standalone && /iPhone|iPad/.test(navigator.userAgent) ? (
            <p className="text-sm text-amber-300">
              Добавь приложение на домашний экран: «Поделиться → На экран Домой». Иначе iOS
              не разрешит подписку на пуши.
            </p>
          ) : (
            <>
              <p className="text-sm text-neutral-400">
                Статус: <span className="text-neutral-200">{labelPermission(permission)}</span>
                {pushOn && ' · подписка активна'}
              </p>
              <div className="flex flex-wrap gap-2">
                {pushOn ? (
                  <Button variant="secondary" disabled={busy} onClick={() => void disablePush()}>
                    <BellOff className="w-4 h-4" /> Отключить
                  </Button>
                ) : (
                  <Button disabled={busy} onClick={() => void enablePush()}>
                    <Bell className="w-4 h-4" /> Включить уведомления
                  </Button>
                )}
                <Button variant="secondary" disabled={busy || !pushOn} onClick={() => void testPush()}>
                  <TestTube2 className="w-4 h-4" /> Тест
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase text-neutral-500">Devices</h2>
        <ul className="space-y-2">
          {devices.map((d) => (
            <li
              key={d.id}
              className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {d.name} {d.current && <span className="text-xs text-blue-400">(это устройство)</span>}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    {d.hasPush ? '🔔 подписано на пуш' : 'без пуша'}
                    {d.lastSeenAt && ` · ${new Date(d.lastSeenAt).toLocaleString('ru-RU')}`}
                  </div>
                </div>
                {!d.current && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      await api.delete(`/api/v1/devices/${d.id}`);
                      await load();
                    }}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <Button variant="secondary" className="w-full" onClick={logout}>
          <LogOut className="w-4 h-4" /> Выйти
        </Button>
      </section>
    </div>
  );
}

function labelPermission(p: NotificationPermission): string {
  if (p === 'granted') return 'разрешено';
  if (p === 'denied') return 'заблокировано в браузере';
  return 'не запрошено';
}
